import { WizardStep, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { compact } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { BasicInfoStep } from 'modules/trainings/components/Shared/WizardComponent/BasicInfoStep'
import { CommentsStep } from 'modules/trainings/components/Shared/WizardComponent/CommentsStep'
import { EmployeeSelectorStep } from 'modules/trainings/components/Shared/WizardComponent/EmployeeSelectorStep'
import { TimingAndCostStep } from 'modules/trainings/components/Shared/WizardComponent/TimingAndCostStep'
import { TrainingNeedStep } from 'modules/trainings/components/Shared/WizardComponent/TrainingNeedStep'
import { TrainingSelectorStep } from 'modules/trainings/components/Shared/WizardComponent/TrainingSelectorStep'
import type {
  WizardConfig,
  WizardData,
} from 'modules/trainings/components/Shared/WizardComponent/types'

interface AmplitudeEvent {
  submit: string
  cancel: string
  back: string
  next: string
  start: string
}

export const useWizardSteps = (
  wizardConfig: WizardConfig,
  showParticipantsStep: boolean,
  handleDraftSubmit: (data: WizardData) => void,
  amplitudeEvent: AmplitudeEvent
): WizardStep<WizardData>[] => {
  const { fromCatalog = false, trainingId, allowEmployeePayment, allowDraft = true } = wizardConfig

  const secondaryAction = allowDraft
    ? {
        action: (data: WizardData) => handleDraftSubmit(data),
        label: i18n.t('trainings.training_requests.create.save_as_draft'),
      }
    : undefined

  const createTrainingRequestWizardSteps = [
    // Course need step
    {
      title: i18n.t('trainings.training_requests.create.training_need_step'),
      nextText: i18n.t('common.next'),
      render: (props: WizardStepRenderParams<WizardData>) => (
        <TrainingNeedStep amplitudeEventNext={amplitudeEvent.next} {...props} />
      ),
      isCompleted: (data: WizardData) => !!data.trainingNeed,
      secondaryAction,
    },

    //Basic information
    !fromCatalog
      ? {
          title: i18n.t('trainings.training_requests.create.basic_info_step'),
          nextText: i18n.t('common.next'),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <BasicInfoStep amplitudeEventNext={amplitudeEvent.next} {...props} />
          ),
          isCompleted: (data: WizardData) => !!data.trainingName,
          secondaryAction,
        }
      : null,

    // Timing and cost
    !fromCatalog
      ? {
          title: i18n.t('trainings.training_requests.create.timing_cost_step.title'),
          nextText: i18n.t('common.next'),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <TimingAndCostStep
              amplitudeEventNext={amplitudeEvent.next}
              allowEmployeePayment={allowEmployeePayment}
              {...props}
            />
          ),
          isCompleted: (data: WizardData) => {
            if (allowEmployeePayment && !!data.cost) {
              if (!data.paymentOption) {
                return false
              }

              return data.paymentOption === 'company' || !!data.paymentInstallments
            }

            return true
          },
          secondaryAction,
        }
      : null,

    // Training
    fromCatalog && !trainingId
      ? {
          title: i18n.t('trainings.training_requests.create.training'),
          nextText: i18n.t('common.next'),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <TrainingSelectorStep amplitudeEventNext={amplitudeEvent.next} {...props} />
          ),
          isCompleted: (data: WizardData) => !!data.trainingId,
          secondaryAction,
        }
      : null,

    // participants
    showParticipantsStep
      ? {
          title: i18n.t('trainings.training_requests.create.employee_selector_step'),
          nextText: i18n.t('common.next'),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <EmployeeSelectorStep amplitudeEventNext={amplitudeEvent.next} {...props} />
          ),
          isCompleted: () => true,
          secondaryAction,
        }
      : null,

    // Comments
    {
      title: i18n.t('trainings.training_requests.create.comments_step'),
      nextText: i18n.t('trainings.training_requests.create.send_for_approval'),
      render: (props: WizardStepRenderParams<WizardData>) => (
        <CommentsStep amplitudeEventNext={amplitudeEvent.next} {...props} />
      ),
      isCompleted: () => true,
      secondaryAction,
    },
  ]

  return compact(createTrainingRequestWizardSteps)
}
